import * as React from "react";
import { FormControl, FormItem, FormMessage } from "../form";
import { Label } from "../label";
import { Popover, PopoverContent, PopoverTrigger } from "../popover";
import { Button } from "../button";
import { cn } from "lib/utils";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { CalendarIcon } from "lucide-react";
import { Calendar } from "../calendar";

interface DatePickerGsProps {
  label?: string;
  fieldValue: Date | undefined;
  fieldOnChange: (...event: any[]) => void;
  minDate?: Date;
  maxDate?: Date;
  showMonthYearPicker?: boolean;
  showTimeSelect?: boolean;
}

const DatePickerGs = React.forwardRef<HTMLBaseElement, DatePickerGsProps>(
  (
    {
      label,
      minDate,
      maxDate,
      fieldValue,
      fieldOnChange,
      showMonthYearPicker,
      showTimeSelect,
    },
    ref
  ) => {
    const [month, setMonth] = React.useState<Date>(fieldValue || new Date());
    const [selectedHour, setSelectedHour] = React.useState<string>(
      fieldValue ? String(fieldValue.getHours()).padStart(2, "0") : "00"
    );
    const [selectedMinute, setSelectedMinute] = React.useState<string>(
      fieldValue ? String(fieldValue.getMinutes()).padStart(2, "0") : "00"
    );

    const handleDateTimeChange = (date: Date | undefined) => {
      if (!date) return;
      const updatedDate = new Date(date);
      updatedDate.setHours(Number(selectedHour));
      updatedDate.setMinutes(Number(selectedMinute));
      fieldOnChange(updatedDate);
    };

    return (
      <FormItem>
        <div className="grid w-full items-center gap-1.5">
          {label && (
            <Label className="font-GilroySemibold text-sm text-slate-800">
              {label}
            </Label>
          )}
          <Popover>
            <PopoverTrigger asChild>
              <FormControl>
                <Button
                  variant={"outline"}
                  className={cn(
                    "font-sans pl-3 text-left border-solid font-normal focus-visible:ring-primary-500",
                    !fieldValue && "text-muted-foreground"
                  )}
                >
                  {fieldValue ? (
                    format(
                      fieldValue,
                      showTimeSelect ? "dd/MM/yyyy HH:mm" : "dd/MM/yyyy",
                      { locale: ptBR }
                    )
                  ) : (
                    <span>Selecione...</span>
                  )}
                  <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                </Button>
              </FormControl>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="single"
              selected={fieldValue}
              onSelect={(date) => {
                if (showTimeSelect) {
                  handleDateTimeChange(date);
                } else {
                  fieldOnChange(date);
                }
              }}
              disabled={(date) =>
                date > (maxDate || new Date()) ||
                date < (minDate || new Date("1900-01-01"))
              }
              month={month}
              onMonthChange={setMonth}
              locale={ptBR}
              captionLayout={showMonthYearPicker ? "dropdown" : undefined}
            />
              {showTimeSelect && (
                <div className="flex justify-center items-center gap-2 p-3 border-t border-slate-200 min-h-[60px]">
                  <select
                    value={selectedHour}
                    onChange={(e) => {
                      setSelectedHour(e.target.value);
                      if (fieldValue) handleDateTimeChange(fieldValue);
                    }}
                    className="rounded-md border px-2 py-1 text-sm"
                  >
                    {Array.from({ length: 24 }).map((_, i) => (
                      <option key={i} value={String(i).padStart(2, "0")}>
                        {String(i).padStart(2, "0")}
                      </option>
                    ))}
                  </select>
                  <span>:</span>
                  <select
                    value={selectedMinute}
                    onChange={(e) => {
                      setSelectedMinute(e.target.value);
                      if (fieldValue) handleDateTimeChange(fieldValue);
                    }}
                    className="rounded-md border px-2 py-1 text-sm"
                  >
                    {Array.from({ length: 60 }).map((_, i) => (
                      <option key={i} value={String(i).padStart(2, "0")}>
                        {String(i).padStart(2, "0")}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </PopoverContent>
          </Popover>
          <FormMessage />
        </div>
      </FormItem>
    );
  }
);

DatePickerGs.displayName = "DatePickerGs";
export default DatePickerGs;
